import { reaction } from "mobx";
import { OVERLAY_ROUTE_NAMES, HYDRATABLE_ROUTES, } from "./OverlayRouteMap";
import { deserializeFromParams, serializeToParams, } from "./OverlayURLSerializer";
/**
 * Connects OverlayStack to the browser History API.
 *
 * The stack is the source of truth; the URL is a projection:
 * - push()    → history.pushState()
 * - replace() → history.replaceState()
 * - pop()     → history.pushState() with new (shorter) state
 * - clear()   → history.pushState() with clean state
 * - popstate  → deserialize URL, restore stack (no history mutation)
 *
 * Non-overlay query params (e.g. cat=, page=) are preserved through all updates.
 */
export class OverlayHistorySync {
    constructor(stack) {
        this.disposer = null;
        this.isHandlingPopstate = false;
        this.stack = stack;
        this.boundHandlePopstate = this.handlePopstate.bind(this);
    }
    /**
     * Hydrate stack from current URL and start listening for history changes.
     */
    start() {
        // Hydrate from current URL on page load — only HYDRATABLE_ROUTES survive
        // a refresh (others depend on in-memory state that is gone after reload).
        const currentParams = new URLSearchParams(window.location.search);
        const entries = deserializeFromParams(currentParams).filter((e) => HYDRATABLE_ROUTES.has(e.route));
        if (entries.length > 0) {
            this.stack.restore(entries);
        }
        // Clean non-hydratable overlay params from the URL so they don't linger after refresh.
        // E.g. ?cart=&added= → ?cart= (added can't survive refresh)
        const hasStaleParams = Array.from(currentParams.keys()).some((key) => OVERLAY_ROUTE_NAMES.has(key) &&
            !HYDRATABLE_ROUTES.has(key));
        if (hasStaleParams) {
            const cleanUrl = this.buildUrl(entries);
            history.replaceState(null, "", cleanUrl);
        }
        // Listen for browser back/forward navigation
        window.addEventListener("popstate", this.boundHandlePopstate);
        // Watch stack changes and update browser URL
        this.disposer = reaction(() => ({
            entries: this.stack.entries,
            lastAction: this.stack.lastAction,
        }), ({ entries, lastAction }) => {
            // Skip: triggered by a popstate event (we're already handling URL change)
            // Skip: triggered by restore (same as popstate)
            // Skip: initial null (no action yet)
            if (this.isHandlingPopstate ||
                lastAction === "restore" ||
                lastAction === null) {
                return;
            }
            const newUrl = this.buildUrl(entries);
            if (lastAction === "replace") {
                history.replaceState(null, "", newUrl);
            }
            else {
                // push, pop, clear — all produce a new history entry
                history.pushState(null, "", newUrl);
            }
        });
    }
    /**
     * Stop syncing and remove all listeners.
     */
    stop() {
        window.removeEventListener("popstate", this.boundHandlePopstate);
        if (this.disposer !== null) {
            this.disposer();
            this.disposer = null;
        }
    }
    handlePopstate() {
        this.isHandlingPopstate = true;
        try {
            const currentParams = new URLSearchParams(window.location.search);
            const entries = deserializeFromParams(currentParams);
            this.stack.restore(entries);
        }
        finally {
            this.isHandlingPopstate = false;
        }
    }
    /**
     * Build a URL string that merges overlay params into the current URL,
     * preserving all non-overlay query params (e.g. cat=, page=).
     */
    buildUrl(entries) {
        const currentParams = new URLSearchParams(window.location.search);
        const overlayParams = serializeToParams(entries);
        const merged = new URLSearchParams();
        // Preserve non-overlay params
        for (const [key, value] of currentParams) {
            if (!OVERLAY_ROUTE_NAMES.has(key)) {
                merged.set(key, value);
            }
        }
        // Add new overlay params
        for (const [key, value] of overlayParams) {
            merged.set(key, value);
        }
        const search = merged.toString();
        return (window.location.pathname +
            (search ? "?" + search : "") +
            window.location.hash);
    }
}
